import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ShieldCheck, ShieldOff, Copy, Loader2 } from "lucide-react";

const inputCls = "rounded-xl border-gray-200 bg-gray-50 text-gray-800 h-10 focus:border-violet-400 focus:ring-1 focus:ring-violet-200 transition-colors";

const SaveBtn = ({ onClick, loading, label, variant = "primary", disabled }) => (
  <button
    onClick={onClick}
    disabled={loading || disabled}
    className={`px-5 py-2 text-sm font-semibold rounded-full transition-colors disabled:opacity-50 ${
      variant === "danger"
        ? "bg-red-50 text-red-600 hover:bg-red-100"
        : variant === "ghost"
        ? "text-gray-500 hover:bg-gray-100"
        : "bg-violet-600 text-white hover:bg-violet-700 shadow-sm shadow-violet-200"
    }`}
  >
    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : label}
  </button>
);

function copyToClipboard(text) {
  navigator.clipboard?.writeText(text).then(
    () => toast.success("Copied"),
    () => toast.error("Couldn't copy — select and copy manually"),
  );
}

/** Two-factor authentication (TOTP) enrollment, status and disable flow.
 * Wired to the backend's /api/auth/mfa/* endpoints (setup/enable/disable/status). */
export default function MfaSettings() {
  const [status, setStatus] = useState(null); // { enabled: bool } | null while loading
  const [step, setStep] = useState("idle"); // idle | setup | backup-codes
  const [secret, setSecret] = useState("");
  const [provisioningUri, setProvisioningUri] = useState("");
  const [code, setCode] = useState("");
  const [backupCodes, setBackupCodes] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showDisable, setShowDisable] = useState(false);
  const [disablePassword, setDisablePassword] = useState("");

  const loadStatus = () => {
    api.get("/auth/mfa/status")
      .then(({ data }) => setStatus(data))
      .catch(() => setStatus({ enabled: false }));
  };

  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/setup");
      setSecret(data.secret);
      setProvisioningUri(data.provisioning_uri);
      setStep("setup");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't start 2FA setup");
    } finally { setBusy(false); }
  };

  const confirmEnable = async () => {
    if (!code.trim()) return toast.error("Enter the 6-digit code from your authenticator app");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/mfa/enable", { code: code.trim() });
      setBackupCodes(data.backup_codes || []);
      setStep("backup-codes");
      setCode("");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Invalid code");
    } finally { setBusy(false); }
  };

  const finishSetup = () => {
    setStep("idle");
    setSecret("");
    setProvisioningUri("");
    setBackupCodes([]);
    toast.success("Two-factor authentication is on");
  };

  const disable = async () => {
    if (!disablePassword) return toast.error("Enter your password to confirm");
    setBusy(true);
    try {
      await api.post("/auth/mfa/disable", { password: disablePassword });
      toast.success("Two-factor authentication turned off");
      setShowDisable(false);
      setDisablePassword("");
      loadStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't disable 2FA");
    } finally { setBusy(false); }
  };

  if (!status) {
    return <div className="text-sm text-gray-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;
  }

  // ── Backup codes step (shown once, right after enabling) ──
  if (step === "backup-codes") {
    return (
      <div className="space-y-4">
        <div className="flex items-start gap-2 text-sm text-gray-700">
          <ShieldCheck className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
          <p>Two-factor authentication is now on. Save these one-time backup codes somewhere safe — each one lets you sign in once if you lose access to your authenticator app. They won't be shown again.</p>
        </div>
        <div className="grid grid-cols-2 gap-2 bg-gray-50 border border-gray-100 rounded-xl p-4">
          {backupCodes.map((c) => (
            <code key={c} className="text-sm font-mono text-gray-800 tracking-wider">{c}</code>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => copyToClipboard(backupCodes.join("\n"))}
            className="flex items-center gap-1.5 text-xs font-semibold text-violet-600 hover:text-violet-800">
            <Copy className="w-3.5 h-3.5" /> Copy all
          </button>
        </div>
        <div className="pt-2 flex justify-end">
          <SaveBtn onClick={finishSetup} label="I've saved these codes" />
        </div>
      </div>
    );
  }

  // ── Setup step (secret + code confirmation) ──
  if (step === "setup") {
    return (
      <div className="space-y-4">
        <div>
          <p className="text-sm text-gray-700 mb-2">1. Add this key to an authenticator app (Google Authenticator, 1Password, Authy, etc.) — look for "enter a setup key manually" if it can't scan a QR code.</p>
          <div className="flex items-center gap-2 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3">
            <code className="flex-1 text-sm font-mono text-gray-800 tracking-widest break-all">{secret}</code>
            <button onClick={() => copyToClipboard(secret)} className="text-gray-400 hover:text-violet-600 shrink-0" title="Copy key">
              <Copy className="w-4 h-4" />
            </button>
          </div>
          {provisioningUri && (
            <button onClick={() => copyToClipboard(provisioningUri)} className="mt-1.5 text-xs text-gray-400 hover:text-violet-600 underline underline-offset-2">
              Copy setup URI instead
            </button>
          )}
        </div>
        <div>
          <Label className="text-xs font-semibold text-gray-600 mb-1.5 block">2. Enter the 6-digit code it generates</Label>
          <Input className={inputCls} value={code} onChange={(e) => setCode(e.target.value)} placeholder="123456" autoFocus maxLength={6} />
        </div>
        <div className="pt-2 flex justify-end gap-2">
          <SaveBtn onClick={() => { setStep("idle"); setCode(""); }} label="Cancel" variant="ghost" />
          <SaveBtn onClick={confirmEnable} loading={busy} label="Confirm & enable" />
        </div>
      </div>
    );
  }

  // ── Idle: status + enable/disable entry points ──
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2">
        <div className="flex items-center gap-2">
          {status.enabled
            ? <ShieldCheck className="w-4 h-4 text-emerald-500" />
            : <ShieldOff className="w-4 h-4 text-gray-300" />}
          <div>
            <div className="text-sm font-medium text-gray-700">
              Two-factor authentication is {status.enabled ? "on" : "off"}
            </div>
            <div className="text-xs text-gray-400">
              {status.enabled
                ? "You'll be asked for a code from your authenticator app when you sign in."
                : "Add an authenticator app as a second factor on sign-in."}
            </div>
          </div>
        </div>
        {status.enabled
          ? <SaveBtn onClick={() => setShowDisable((v) => !v)} label="Disable" variant="danger" />
          : <SaveBtn onClick={startSetup} loading={busy} label="Enable 2FA" />}
      </div>

      {showDisable && (
        <div className="bg-red-50/50 border border-red-100 rounded-xl p-4 space-y-3">
          <p className="text-xs text-red-600">Enter your password to turn off two-factor authentication.</p>
          <Input type="password" className={inputCls} value={disablePassword}
            onChange={(e) => setDisablePassword(e.target.value)} placeholder="Current password" />
          <div className="flex justify-end gap-2">
            <SaveBtn onClick={() => { setShowDisable(false); setDisablePassword(""); }} label="Cancel" variant="ghost" />
            <SaveBtn onClick={disable} loading={busy} label="Confirm disable" variant="danger" />
          </div>
        </div>
      )}
    </div>
  );
}
